<template>
  <div>
    <!-- New Supplier Popup -->
    <Dialog :open="isSupplierPopup" @update:open="isSupplierPopup = $event">
      <DialogContent class="sm:tw-max-w-[800px]">
        <DialogHeader>
          <DialogTitle>{{ t('suppliers.titles.add_new_supplier') }}</DialogTitle>
        </DialogHeader>
        <!-- Supplier Form -->
        <form @submit.prevent="addNewSupplier">
          <!-- NAME + SUPPLIER NUMBER -->
          <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
            <div>
              <Label>
                {{ t('suppliers.titles.supplier_name') }}
                <span class="tw-text-red-600">*</span>
              </Label>
              <Input
                v-model="supplierName"
                name="supplierName"
                type="text"
                :class="{ 'tw-border-danger': errors.supplierName }"
                :placeholder="t('suppliers.titles.name_placeholder') + '...'"
              />
              <div v-if="errors.supplierName" class="tw-text-danger tw-text-xs tw-mt-1">
                {{ errors.supplierName }}
              </div>
            </div>
            <div>
              <Label>
                {{ t('suppliers.titles.supplier_number') }}
                <span class="tw-text-red-600">*</span>
              </Label>
              <Input
                v-model="supplierNumber"
                name="supplierNumber"
                type="text"
                :class="{ 'tw-border-danger': errors.supplierNumber }"
                :placeholder="t('suppliers.titles.number_placeholder') + '...'"
              />
              <div v-if="errors.supplierNumber" class="tw-text-danger tw-text-xs tw-mt-1">
                {{ errors.supplierNumber }}
              </div>
            </div>
          </div>
          <!-- CUSTOMER since it's required-->
          <input hidden :value="formData.customer" />
          <!-- ADDRESS + ZIP CODE -->
          <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
            <div>
              <Label>{{ t('suppliers.titles.address') }}</Label>
              <Input
                v-model="address"
                name="address"
                type="text"
                :class="{ 'tw-border-danger': errors.address }"
                :placeholder="t('suppliers.titles.address_placeholder') + '...'"
              />
              <div v-if="errors.address" class="tw-text-danger tw-text-xs tw-mt-1">
                {{ errors.address }}
              </div>
            </div>
            <div>
              <Label>{{ t('suppliers.titles.zip_code') }}</Label>
              <Input
                v-model="zipCode"
                name="zipCode"
                type="text"
                :class="{ 'tw-border-danger': errors.zipCode }"
                :placeholder="t('suppliers.titles.zip_code_placeholder') + '...'"
              />
              <div v-if="errors.zipCode" class="tw-text-danger tw-text-xs tw-mt-1">
                {{ errors.zipCode }}
              </div>
            </div>
          </div>
          <!-- COUNTRY + CITY -->
          <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
            <div>
              <Label>{{ t('suppliers.titles.country') }}</Label>
              <SearchableSelect
                v-model="country"
                :options="countriesList"
                value-prop="name"
                label-prop="displayed_name"
                :placeholder="t('suppliers.titles.country_placeholder') + '...'"
                :class="{ 'tw-border-danger': errors.country }"
              />
              <div v-if="errors.country" class="tw-text-danger tw-text-xs tw-mt-1">
                {{ errors.country }}
              </div>
            </div>
            <div>
              <Label>{{ t('suppliers.titles.city') }}</Label>
              <SearchableSelect
                v-model="city"
                :options="citiesList"
                value-prop="name"
                label-prop="name"
                :placeholder="t('suppliers.titles.city_placeholder') + '...'"
                :class="{ 'tw-border-danger': errors.city }"
              />
              <div v-if="errors.city" class="tw-text-danger tw-text-xs tw-mt-1">
                {{ errors.city }}
              </div>
            </div>
          </div>
          <!-- PHONE + EMAIL -->
          <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
            <div>
              <Label>{{ t('suppliers.titles.phone') }}</Label>
              <PhoneInput
                v-model="phone"
                :placeholder="t('suppliers.titles.phone_placeholder')"
                code="DE"
                :disabled="false"
                :class="{ 'tw-border-danger': errors.phone }"
              />
              <div v-if="errors.phone" class="tw-text-danger tw-text-xs tw-mt-1">
                {{ errors.phone }}
              </div>
            </div>
            <div>
              <Label>{{ t('suppliers.titles.email') }}</Label>
              <Input
                v-model="supplierEmail"
                name="supplierEmail"
                type="email"
                :class="{ 'tw-border-danger': errors.supplierEmail }"
                :placeholder="t('suppliers.titles.email_placeholder') + '...'"
              />
              <div v-if="errors.supplierEmail" class="tw-text-danger tw-text-xs tw-mt-1">
                {{ errors.supplierEmail }}
              </div>
            </div>
          </div>
          <!-- Supplier Alerts -->
          <div v-if="supplierCreatedSuccessfully != null" class="tw-mb-4">
            <Alert v-if="supplierCreatedSuccessfully && !meta.dirty" class="lg:tw-w-7/12">
              <AlertDescription>{{ t('suppliers.titles.create_success') }}</AlertDescription>
            </Alert>
            <Alert v-if="!supplierCreatedSuccessfully" variant="destructive" class="lg:tw-w-7/12">
              <AlertDescription>{{ errorSupplierMessage }}</AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" @click="closeNewSupplierModal">
              {{ t('common.forms.cancel') }}
            </Button>
            <CustomerActionButton
              :disabled="isSubmitting"
              type="submit"
              class="tw-bg-primary-500 tw-text-secondary-900 tw-font-semibold hover:tw-bg-primary-600 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-flex tw-items-center tw-gap-2"
            >
              <Loader2 v-if="isSubmitting" class="tw-h-4 tw-w-4 tw-animate-spin" />
              {{ t('suppliers.titles.add_supplier') }}
            </CustomerActionButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    <!-- Cancel Dialog -->
    <Dialog :open="isCancelPopup" @update:open="isCancelPopup = $event">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{{ t('common.forms.delete') }}</DialogTitle>
        </DialogHeader>
        <h5>{{ t('payment.titles.confirm_discard') }}</h5>
        <DialogFooter>
          <Button @click="onChangePaymentCancel">
            {{ t('common.forms.cancel_discard') }}
          </Button>
          <Button variant="secondary" @click="closePopup">
            {{ t('common.forms.cancel') }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- Sticky action bar -->
    <FormActionBar :title="t('payment.titles.new_payment')">
      <template #meta>
        <span
          v-if="!currentUser?.is_admin"
          class="tw-hidden tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-surface-200 tw-bg-white tw-px-3 tw-py-1.5 sm:tw-inline-flex dark:tw-border-neutral-800 dark:tw-bg-neutral-950"
        >
          <span class="tw-text-xs tw-text-surface-500 dark:tw-text-neutral-400">
            {{ t('orders.titles.current_balance') }}
          </span>
          <span
            class="tw-text-sm tw-font-semibold tw-tabular-nums"
            :class="currentBalance >= 0 ? 'tw-text-emerald-600' : 'tw-text-red-600'"
          >
            {{ n(currentBalance, 'currency') }}
          </span>
        </span>
      </template>

      <template #actions>
        <Button
          type="button"
          variant="outline"
          size="sm"
          :disabled="isMainSubmitting || isSavingDraft"
          @click="onCancel()"
        >
          {{ t('common.forms.cancel_discard') }}
        </Button>
        <CustomerActionButton
          v-if="
            !isEditPayment ||
            (isEditPayment && payments.length && !payments[0].is_clarification_requested)
          "
          :loading="isSavingDraft"
          class="tw-rounded-md tw-bg-secondary-500 tw-px-3.5 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-secondary-600"
          @click="runDraft"
        >
          {{ t('orders.forms.save_draft') }}
        </CustomerActionButton>
        <CustomerActionButton
          :loading="isMainSubmitting"
          class="tw-rounded-md tw-bg-primary-500 tw-px-3.5 tw-py-2 tw-text-sm tw-font-semibold tw-text-secondary-900 hover:tw-bg-primary-600"
          @click.prevent="runSubmit"
        >
          {{ currentUser?.is_admin ? t('common.forms.submit_sign') : t('common.forms.submit') }}
        </CustomerActionButton>
      </template>

      <template #banner>
        <div
          v-if="
            isCreatedSuccessfully != null ||
            isEditSuccessfully !== null ||
            isDraftSuccessfully !== null ||
            isUploadError
          "
          class="tw-mt-3 tw-space-y-2"
        >
          <template
            v-if="
              isCreatedSuccessfully != null ||
              isEditSuccessfully !== null ||
              isDraftSuccessfully !== null
            "
          >
            <Alert v-if="isCreatedSuccessfully" class="lg:tw-w-7/12">
              <AlertDescription>
                {{ t('customer_management.messages.order_forms_create_success') }}
              </AlertDescription>
            </Alert>
            <Alert v-else-if="isEditSuccessfully" class="lg:tw-w-7/12">
              <AlertDescription>{{ t('payment.titles.update_success') }}</AlertDescription>
            </Alert>
            <Alert v-else-if="isDraftSuccessfully" class="lg:tw-w-7/12">
              <AlertDescription>{{ t('payment.titles.saved_as_draft') }}</AlertDescription>
            </Alert>
            <Alert v-else variant="destructive" class="lg:tw-w-7/12">
              <AlertDescription>{{ errorMessage }}</AlertDescription>
            </Alert>
          </template>
          <Alert v-if="isUploadError" variant="destructive" class="lg:tw-w-5/12">
            <AlertDescription>{{ t('payment.titles.max_file_size_hint') }}</AlertDescription>
          </Alert>
        </div>
      </template>
    </FormActionBar>

    <!-- Payment Form -->
    <div
      v-if="formData.customer === null"
      class="tw-py-10 tw-text-center tw-text-sm tw-text-surface-500 dark:tw-text-neutral-400"
    >
      {{ t('payment.titles.select_customer_hint') }}
    </div>

    <!-- Payment Info -->
    <div v-else>
      <!-- Per-payment Card -->
      <EntitySplitLayout
        v-for="(item, index) in formData.paymentCount"
        :key="index"
        storage-key="payment-form-split-size-v2"
        :default-document-size="67"
        :min-document-size="50"
        :max-document-size="67"
      >
        <template #documents>
          <DocumentManagerPanel
            :files="getExistingFiles(index, payments[index].files || [])"
            :pending-files="getPendingFiles(index)"
            editable
            @add="files => addFiles(files, index)"
            @remove="removeFile"
          />
          <input
            type="file"
            :id="`take-picture-${index}`"
            class="tw-hidden"
            accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
            capture="environment"
            multiple
            @change="event => handleCameraCapture(event, index)"
          />
        </template>
        <template #details>
          <div class="tw-pr-2 lg:tw-flex-1 lg:tw-min-h-0 lg:tw-overflow-y-auto">
            <!-- Section: Project -->
            <FormSectionHeader :title="t('requests.titles.project')" first>
              <ProjectSelect
                v-model="formData.project"
                :customer-id="newCustomerId"
                :placeholder="t('orders.forms.select_project')"
                class="tw-max-w-md"
              />
            </FormSectionHeader>

            <!-- Section: Supplier Details -->
            <FormSectionHeader
              :title="t('payment.sections.supplier_details')"
              :force-expanded="isErrorFound"
            >
              <!-- Supplier -->
              <div class="tw-mb-4">
                <Label>
                  {{ t('orders.titles.supplier') }}
                  <span class="tw-text-red-600">*</span>
                </Label>
                <div class="tw-flex tw-gap-2">
                  <SupplierSelect
                    v-if="newCustomerId"
                    ref="supplierSelectRef"
                    class="tw-flex-1"
                    v-model="payments[index].supplier"
                    :customer-id="newCustomerId"
                    :placeholder="t('orders.forms.select_supplier')"
                    :action-label="t('orders.titles.add_supplier')"
                    @action="openNewSupplierModal(index)"
                    @change="(s: any) => { if (s) payments[index].order_no_supplier = s.supplier_number; }"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    class="tw-h-9 tw-w-9 tw-shrink-0"
                    @click="openNewSupplierModal(index)"
                  >
                    <Plus class="tw-h-4 tw-w-4" />
                  </Button>
                </div>
                <div
                  v-if="isErrorFound && paymentErrors[index].supplier !== ''"
                  class="tw-text-danger tw-text-xs tw-mt-1"
                >
                  {{ paymentErrors[index].supplier }}
                </div>
              </div>

              <!-- Supplier Order No. (alone, read-only) -->
              <div class="tw-mb-4">
                <div>
                  <Label>
                    {{ t('orders.titles.supplier_no') }}
                    <span class="tw-text-red-600">*</span>
                  </Label>
                  <Input
                    type="text"
                    readonly
                    class="!tw-bg-neutral-100 !tw-text-neutral-500 !tw-cursor-default !tw-border-neutral-200/50 !tw-shadow-none"
                    :model-value="payments[index].order_no_supplier"
                  />
                </div>
              </div>
            </FormSectionHeader>

            <!-- Section: Invoice Information -->
            <FormSectionHeader
              :title="t('payment.sections.invoice_information')"
              :force-expanded="isErrorFound"
            >
              <!-- Invoice No. + Invoice Date -->
              <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-mb-4">
                <div>
                  <Label>
                    {{ t('payment.titles.invoice_no') }}
                    <span class="tw-text-red-600">*</span>
                  </Label>
                  <Input
                    type="text"
                    v-model="payments[index].invoice_number"
                    :placeholder="t('payment.titles.invoice_no')"
                  />
                  <span
                    v-if="isErrorFound && paymentErrors[index].invoice_number !== ''"
                    class="tw-text-danger tw-text-xs tw-mt-1"
                  >
                    {{ paymentErrors[index].invoice_number }}
                  </span>
                </div>
                <div>
                  <Label>
                    {{ t('payment.titles.invoice_date') }}
                    <span class="tw-text-red-600">*</span>
                  </Label>
                  <DatePicker
                    v-model="payments[index].invoice_date"
                    :placeholder="t('payment.titles.invoice_date')"
                  />
                  <span
                    v-if="isErrorFound && paymentErrors[index].invoice_date !== ''"
                    class="tw-text-danger tw-text-xs tw-mt-1"
                  >
                    {{ paymentErrors[index].invoice_date }}
                  </span>
                </div>
              </div>

              <!-- Due Date + Payment Date -->
              <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-mb-4">
                <div>
                  <Label>
                    {{ t('payment.titles.due_date') }}
                    <span class="tw-text-red-600">*</span>
                  </Label>
                  <DatePicker
                    v-model="payments[index].due_date"
                    :placeholder="t('payment.titles.due_date')"
                  />
                  <span
                    v-if="isErrorFound && paymentErrors[index].due_date !== ''"
                    class="tw-text-danger tw-text-xs tw-mt-1"
                  >
                    {{ paymentErrors[index].due_date }}
                  </span>
                </div>
                <div>
                  <Label>
                    {{ t('payment.titles.payment_date') }}
                    <span v-if="mandatoryFields.includes('payment_date')" class="tw-text-red-600">
                      *
                    </span>
                  </Label>
                  <DatePicker
                    v-model="payments[index].payment_date"
                    :placeholder="t('payment.titles.payment_date')"
                  />
                  <span
                    v-if="isErrorFound && paymentErrors[index].payment_date !== ''"
                    class="tw-text-danger tw-text-xs tw-mt-1"
                  >
                    {{ paymentErrors[index].payment_date }}
                  </span>
                </div>
              </div>
            </FormSectionHeader>

            <!-- Section: Amounts -->
            <FormSectionHeader
              :title="t('payment.sections.amounts')"
              :force-expanded="isErrorFound"
            >
              <!-- Net / VAT / Gross field group with auto-calculation -->
              <div class="tw-mb-4">
                <NetVatGrossFields
                  v-model:net="payments[index].net_eur"
                  v-model:gross="payments[index].amount"
                  v-model:vat-rate="payments[index].vat_rate"
                  :net-error="isErrorFound ? paymentErrors[index].net_eur : ''"
                  :gross-error="isErrorFound ? paymentErrors[index].amount : ''"
                />
              </div>

              <!-- Discount + Gross After Discount -->
              <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-mb-4">
                <div>
                  <Label>
                    {{ t('payment.titles.discount') }} (%)
                    <span class="tw-text-red-600">*</span>
                  </Label>
                  <Input
                    type="text"
                    v-model="payments[index].discount"
                    :placeholder="t('payment.titles.discount')"
                  />
                  <span
                    v-if="isErrorFound && paymentErrors[index].discount !== ''"
                    class="tw-text-danger tw-text-xs tw-mt-1"
                  >
                    {{ paymentErrors[index].discount }}
                  </span>
                </div>
                <div>
                  <Label>
                    {{ t('payment.titles.gross_amount_after_discount') }}
                    <span class="tw-text-red-600">*</span>
                  </Label>
                  <CurrencyInput
                    v-model="payments[index].gross_eur_less_discount"
                    :placeholder="t('payment.titles.gross_amount_after_discount')"
                  />
                  <span
                    v-if="isErrorFound && paymentErrors[index].gross_eur_less_discount !== ''"
                    class="tw-text-danger tw-text-xs tw-mt-1"
                  >
                    {{ paymentErrors[index].gross_eur_less_discount }}
                  </span>
                </div>
              </div>

              <!-- Comparison Gross EUR -->
              <div class="tw-mb-4">
                <Label>{{ t('payment.titles.gross_comparison_amount') }}</Label>
                <CurrencyInput
                  v-model="payments[index].comparison_gross_eur_order_list"
                  :readonly="currentUser?.user_role.includes('Payer') ? false : true"
                  :placeholder="t('payment.titles.gross_comparison_amount')"
                  :class="
                    !currentUser?.user_role.includes('Payer')
                      ? '!tw-bg-neutral-100 !tw-text-neutral-500 !tw-cursor-default !tw-border-neutral-200/50 !tw-shadow-none'
                      : ''
                  "
                />
              </div>
            </FormSectionHeader>

            <!-- Section: Additional Details (dynamic fields) -->
            <FormSectionHeader
              v-if="formFields.length > 0"
              :title="t('payment.sections.additional_details')"
              :force-expanded="isErrorFound"
            >
              <!-- Dynamic Form Fields (paired) -->
              <div
                v-for="(fieldPair, pairIdx) in Array.from(
                  { length: Math.ceil(formFields.length / 2) },
                  (_, i) => formFields.slice(i * 2, i * 2 + 2)
                )"
                :key="pairIdx"
                class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-mb-4"
              >
                <div v-for="field in fieldPair" :key="field.field.model_field_name">
                  <Label>
                    {{ field.field.field_name }}
                    <span class="tw-text-danger" v-if="field.is_mandatory">*</span>
                  </Label>
                  <!-- Input Text Fields -->
                  <Input
                    v-if="field.field.data_type === 'TEXT'"
                    type="text"
                    :placeholder="'Enter ' + field.field.field_name"
                    v-model="payments[index][field.field.model_field_name]"
                  />
                  <!-- Input Number Fields -->
                  <Input
                    v-else-if="field.field.data_type === 'INTEGER'"
                    type="text"
                    :placeholder="'Enter ' + field.field.field_name"
                    min="0"
                    v-model="payments[index][field.field.model_field_name]"
                  />
                  <!-- Date Picker -->
                  <DatePicker
                    v-else-if="field.field.data_type === 'DATE'"
                    :placeholder="'Enter ' + field.field.field_name"
                    v-model="payments[index][field.field.model_field_name]"
                  />
                  <!-- (Boolean) Radio = Masse Liability -->
                  <div class="tw-flex tw-flex-wrap" v-else-if="field.field.data_type === 'BOOLEAN'">
                    <div class="md:tw-w-1/2">
                      <div class="tw-flex tw-items-center">
                        <input
                          type="radio"
                          :name="`${field.field.model_field_name}-${index}`"
                          :value="true"
                          v-model="payments[index][field.field.model_field_name]"
                          :id="`${field.field.model_field_name}-yes-${index}`"
                          class="tw-mr-1"
                          style="accent-color: rgb(var(--secondary-500))"
                        />
                        <label
                          class="tw-mb-0"
                          :for="`${field.field.model_field_name}-yes-${index}`"
                        >
                          {{ t('common.labels.yes') }}
                        </label>
                      </div>
                    </div>
                    <div class="md:tw-w-1/2">
                      <div class="tw-flex tw-items-center">
                        <input
                          type="radio"
                          :name="`${field.field.model_field_name}-${index}`"
                          :value="false"
                          v-model="payments[index][field.field.model_field_name]"
                          :id="`${field.field.model_field_name}-no-${index}`"
                          class="tw-mr-1"
                          style="accent-color: rgb(var(--secondary-500))"
                        />
                        <label class="tw-mb-0" :for="`${field.field.model_field_name}-no-${index}`">
                          {{ t('common.data.number_title') }}
                        </label>
                      </div>
                    </div>
                  </div>
                  <span
                    v-if="isErrorFound && paymentErrors[index][field.field.model_field_name] !== ''"
                    class="tw-text-danger tw-text-xs tw-mt-1"
                  >
                    {{ paymentErrors[index][field.field.model_field_name] }}
                  </span>
                </div>
              </div>
            </FormSectionHeader>

            <!-- Section: Reference (edit mode only) -->
            <FormSectionHeader
              v-if="isEditPayment"
              :title="t('payment.sections.reference')"
              :force-expanded="isErrorFound"
            >
              <!-- Sequence Order No. (alone, only in edit mode) -->
              <div class="tw-mb-4">
                <div>
                  <Label>{{ t('payment.titles.sequence_order_no') }}</Label>
                  <Input
                    type="text"
                    readonly
                    class="!tw-bg-neutral-100 !tw-text-neutral-500 !tw-cursor-default !tw-border-neutral-200/50 !tw-shadow-none"
                    :model-value="payments[index].sequence_order_no"
                    :placeholder="t('payment.titles.sequence_order_no')"
                  />
                  <span
                    v-if="isErrorFound && paymentErrors[index]?.sequence_order_no !== ''"
                    class="tw-text-danger tw-text-xs tw-mt-1"
                  >
                    {{ paymentErrors[index]?.sequence_order_no }}
                  </span>
                </div>
              </div>
            </FormSectionHeader>
            <!-- Section: Orders Table -->
            <FormSectionHeader :title="t('orders.titles.orders')">
              <div class="tw-overflow-x-auto">
                <SelectOrdersForPaymentTable
                  :key="selectedOrdersChangeKey"
                  :index="index"
                  :customer-id="formData.customer"
                  :is-dashboard="false"
                  @order-selected="updateSelectedOrders"
                  @get-current-index="getCurrentIndex"
                  :orders="paymentOrders"
                />
              </div>
              <span
                v-if="isErrorFound && paymentErrors[index].orders !== ''"
                class="tw-text-danger tw-text-xs tw-mt-1"
              >
                {{ paymentErrors[index].orders }}
              </span>
            </FormSectionHeader>
          </div>
        </template>
      </EntitySplitLayout>
    </div>
    <!-- payment adjustment popup -->
    <adjust-payment-for-submission
      v-model="isPaymentAdjustment"
      :payments-data="paymentsNeedAdjustment"
      @close-adjustment-modal="closeAdjustmentModal"
      @adjust-payments="adjustPaymentsAndSubmit"
    ></adjust-payment-for-submission>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, nextTick, type Ref, watch } from 'vue';

import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import NetVatGrossFields from '@/components/shared/net-vat-gross/NetVatGrossFields.vue';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import EntitySplitLayout from '@/components/shared/entity-detail/EntitySplitLayout.vue';
import { DocumentManagerPanel } from '@/components/shared/documents';
import { useEntityAttachments } from '@/composables/useEntityAttachments';
import { DatePicker } from '@/components/ui/date-picker';
import { Trash2, Plus, Loader2 } from 'lucide-vue-next';
import { useCustomerStore } from '@/stores/customer.store';
import { useUserStore } from '@/stores/user.store';
import { storeToRefs } from 'pinia';
import SelectOrdersForPaymentTable from '@/components/shared/tables/payments-table/SelectOrdersForPaymentTable.vue';
import router from '@/router';
import { useRouter, useRoute } from 'vue-router';
const route = useRoute();
import CurrencyInput from '@/components/shared/currency-input/CurrencyInput.vue';
import moment from 'moment';
import axios from 'axios';
import { usePaymentStore } from '@/stores/payment.store';
import { useSupplierStore } from '@/stores/supplier.store';
import { useSupplierForm } from '@/_helpers/composables/master-data/supplier-changing.composable';
import { useOrderStore } from '@/stores/order.store';
import AdjustPaymentForSubmission from '@/components/payments/AdjustPaymentForSubmission.vue';
import { useI18n } from 'vue-i18n';
import { debounce } from 'lodash';
import CustomerActionButton from '@/components/shared/CustomerActionButton.vue';
import { SearchableSelect } from '@/components/ui/searchable-select';
import SupplierSelect from '@/components/shared/SupplierSelect.vue';
import ProjectSelect from '@/components/shared/ProjectSelect.vue';
import { PhoneInput } from '@/components/ui/phone-input';
import { FormSectionHeader } from '@/components/ui/form-section-header';
import FormActionBar from '@/components/shared/forms/FormActionBar.vue';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import cookieService from '@/_helpers/services/cookie.service';

const { getCustomersOptions } = useCustomerStore();
const { currentUser } = storeToRefs(useUserStore());
const { getSuppliersList, createSupplier } = useSupplierStore();
const { getCustomerCurrentBalance } = useOrderStore();
const { t, n } = useI18n();
const { setBreadcrumbs } = useBreadcrumbs();

const {
  errors,
  handleSubmit,
  isSubmitting,
  meta,
  resetForm,
  supplierName,
  supplierNumber,
  address,
  zipCode,
  country,
  city,
  phone,
  supplierEmail,
  countriesList,
  citiesList,
  setFieldValue,
} = useSupplierForm();

const { createPayment, createPaymentFiles, getPaymentById, editPayment, deletePaymentFile } =
  usePaymentStore();

const {
  getExistingFiles,
  getPendingFiles,
  addFiles,
  removeFile,
  getPendingUploadsForIndex,
  deletedFileIds,
} = useEntityAttachments();

const uploadAttachmentsForCreatedPayments = async (createdPayments: any[]) => {
  await Promise.all(
    createdPayments.flatMap((payment, index) => {
      const pending = getPendingUploadsForIndex(index);
      return pending.length ? [createPaymentFiles(payment.id, pending)] : [];
    })
  );
};

const syncPaymentAttachmentChanges = async (paymentId: number, index = 0) => {
  const pending = getPendingUploadsForIndex(index);
  if (pending.length) {
    await createPaymentFiles(paymentId, pending);
  }
  if (deletedFileIds.value.length) {
    await Promise.all(deletedFileIds.value.map(id => deletePaymentFile(id)));
  }
};

const handleCameraCapture = (event: Event, index: number) => {
  const target = event.target as HTMLInputElement;
  if (target.files?.length) {
    addFiles(Array.from(target.files), index);
    target.value = '';
  }
};

let customersList: any = ref([]);

const isCancelPopup = ref(false);

const isSupplierPopup = ref(false);

const isUploadError = ref(false);

const isEditPayment = ref(false);

const selectedOrdersChangeKey = ref(0);

let editPaymentId = ref(0);

let deletedFileId = ref(null);

let fileCount = ref(0);

const newFiles: any = ref([
  {
    file: null,
    index: null,
    file_name: null,
    file_size: null,
  },
]);

let currentindex = ref(0);

const isCreatedSuccessfully: Ref<boolean | null> = ref(null);

const isEditSuccessfully: Ref<boolean | null> = ref(null);

const isDraftSuccessfully: Ref<boolean | null> = ref(null);

const formFields: any = ref([]);

const currentBalance: any = ref(0);

const remainingBalance: any = ref(0);

const newCustomerId: any = ref(null);

const isPaymentAdjustment: any = ref(false);

const paymentsNeedAdjustment: any = ref([]);

const uuidv4 = () => {
  return URL.createObjectURL(new Blob()).substring(36);
};

let payments: any = ref([
  {
    id: uuidv4(),
    project: null,
    customer: null,
    amount: null,
    status: 'PENDING_APPROVAL',
    payment_date: null,
    sequence_order_no: '',
    pay_no: null,
    order_type: null,
    internal_order_number: '',
    vendor_no: null,
    vendor: null,
    payment_term: null,
    invoice_number: null,
    invoice_date: null,
    due_date: null,
    service_date: null,
    net_eur: null,
    gross_eur: null,
    vat_rate: null,
    comparison_gross_eur_order_list: null,
    total_order_balance: null,
    discount: 0,
    gross_eur_less_discount: null,
    comment: null,
    date: null,
    comment_allea: null,
    comment_management: null,
    comment_insv: null,
    masse_liability: false,
    submit: false,
    document: null,
    url: null,
    orders: [],
    orders_verbose: [],
    end_after: null,
    frequency: '',
    name: '',
    is_recurring: false,
    supplier: null,
  },
]);

let formData: any = reactive({
  customer: null,
  project: null,
  paymentCount: payments.value.length,
  invoiceName: '',
  startDate: null,
  requestType: 'one-time',
  frequency: '',
  endsAfter: 0,
});

const errorMessage: any = ref('');

const rules = {
  customer: {
    required,
  },
};

const BASE_MANDATORY_FIELDS = [
  'supplier',
  'invoice_number',
  'due_date',
  'invoice_date',
  'net_eur',
  'amount',
  'discount',
  'gross_eur_less_discount',
];

const CORE_FIELD_NAMES = [
  ...BASE_MANDATORY_FIELDS,
  'payment_date',
  'date',
  'comparison_gross_eur_order_list',
  'sequence_order_number',
];

const mandatoryFields: any = ref([...BASE_MANDATORY_FIELDS]);

const supplierCreatedSuccessfully: Ref<boolean | null> = ref(null);

const errorSupplierMessage = ref('');

const paymentSupplierIndex = ref(0);

const paymentOrders = ref([]);

let isErrorFound = ref(false);

let paymentErrors: any = ref([
  {
    project: '',
    customer: '',
    amount: '',
    status: '',
    payment_date: '',
    sequence_order_no: '',
    pay_no: '',
    order_type: '',
    internal_order_number: '',
    vendor_no: '',
    vendor: '',
    payment_term: '',
    invoice_number: '',
    invoice_date: '',
    due_date: '',
    service_date: '',
    net_eur: '',
    gross_eur: '',
    comparison_gross_eur_order_list: '',
    discount: '',
    gross_eur_less_discount: '',
    comment: '',
    date: '',
    comment_allea: '',
    comment_management: '',
    comment_insv: '',
    masse_liability: '',
    submit: '',
    document: '',
    url: '',
    orders: '',
    end_after: '',
    frequency: '',
    name: '',
    is_recurring: '',
    supplier: '',
  },
]);

const $v = useVuelidate(rules, formData);

/* Get Project By Customer id*/

const add = () => {
  formData.paymentCount++;
  payments.value.push({
    id: uuidv4(),
    project: null,
    customer: null,
    amount: null,
    status: 'PENDING_APPROVAL',
    payment_date: null,
    sequence_order_no: '',
    pay_no: null,
    order_type: null,
    internal_order_number: '',
    vendor_no: null,
    vendor: null,
    payment_term: null,
    invoice_number: null,
    invoice_date: null,
    due_date: null,
    service_date: null,
    net_eur: null,
    gross_eur: null,
    vat_rate: null,
    comparison_gross_eur_order_list: null,
    total_order_balance: null,
    discount: 0,
    gross_eur_less_discount: null,
    comment: null,
    date: null,
    comment_allea: null,
    comment_management: null,
    comment_insv: null,
    masse_liability: false,
    submit: false,
    document: null,
    url: null,
    orders: [],
    orders_verbose: [],
    end_after: null,
    frequency: '',
    name: '',
    is_recurring: false,
    supplier: null,
  });
  paymentErrors.value.push({
    project: '',
    customer: '',
    amount: '',
    status: '',
    payment_date: '',
    sequence_order_no: '',
    pay_no: '',
    order_type: '',
    internal_order_number: '',
    vendor_no: '',
    vendor: '',
    payment_term: '',
    invoice_number: '',
    invoice_date: '',
    due_date: '',
    service_date: '',
    net_eur: '',
    gross_eur: '',
    comparison_gross_eur_order_list: '',
    discount: '',
    gross_eur_less_discount: '',
    comment: '',
    date: '',
    comment_allea: '',
    comment_management: '',
    comment_insv: '',
    masse_liability: '',
    submit: '',
    document: '',
    url: '',
    orders: '',
    end_after: '',
    frequency: '',
    name: '',
    is_recurring: '',
    supplier: '',
  });
};
const subtract = () => {
  formData.paymentCount--;
  if (formData.paymentCount < 1) {
    formData.paymentCount = 1;
    return;
  }
  payments.value.pop();
};

watch(
  () => formData.customer,
  async (newVal, oldVal) => {
    formFields.value = [];
    mandatoryFields.value = [...BASE_MANDATORY_FIELDS];
    await axios
      .get('/payments/customer_payment_fields/?customer=' + newVal)
      .then(res => {
        res.data.results.map((item: any) => {
          const fieldName = item.field?.model_field_name;
          if (!CORE_FIELD_NAMES.includes(fieldName)) {
            formFields.value.push(item);
          }
          if (item.is_mandatory) {
            mandatoryFields.value.push(fieldName);
          }
        });

        /* sort array */
        formFields.value.sort((a, b) => {
          const fieldA = a.field.field_name.toUpperCase();
          const fieldB = b.field.field_name.toUpperCase();

          if (fieldA < fieldB) {
            return -1;
          }
          if (fieldA > fieldB) {
            return 1;
          }
          return 0;
        });
      })
      .catch(error => console.log(error));

    newCustomerId.value = newVal;
    if (newVal !== null) {
      await getCustomerCurrentBalance(newVal, null).then(response => {
        currentBalance.value = response.current_balance;
        remainingBalance.value = response.current_balance;
      });
    }
    if (oldVal !== null) {
      // empty supplier selection
      payments.value = payments.value.map(payment => {
        return { ...payment, supplier: null };
      });
    }
  }
);

watch(
  () => formData.project,
  async newVal => {
    if (newCustomerId.value !== null) {
      await getCustomerCurrentBalance(newCustomerId.value, newVal).then(response => {
        currentBalance.value = response.current_balance;
        remainingBalance.value = response.current_balance;
      });
    }
  }
);

const _discountCalcLock = ref(false);

watch(
  () =>
    payments.value.map((p: any) => ({
      amount: p.amount,
      discount: p.discount,
      gross_eur_less_discount: p.gross_eur_less_discount,
    })),
  (newVals, oldVals) => {
    if (_discountCalcLock.value) return;
    _discountCalcLock.value = true;

    const round2 = (n: number) => Math.round(n * 100) / 100;

    newVals.forEach((val: any, idx: number) => {
      const old = oldVals?.[idx];
      if (!old) return;
      const payment = payments.value[idx];

      const g = parseFloat(val.amount);
      const d = parseFloat(val.discount);
      const a = parseFloat(val.gross_eur_less_discount);
      const hasG = !isNaN(g);
      const hasD = !isNaN(d);
      const hasA = !isNaN(a);

      const gChanged = val.amount !== old.amount;
      const dChanged = val.discount !== old.discount;
      const aChanged = val.gross_eur_less_discount !== old.gross_eur_less_discount;

      if (gChanged && !aChanged) {
        if (hasG && hasD) {
          payment.gross_eur_less_discount = round2(g * (1 - d / 100));
        } else if (hasG && hasA) {
          payment.discount = g !== 0 ? round2((1 - a / g) * 100) : 0;
        }
      } else if (dChanged && !aChanged) {
        if (hasG && hasD) {
          payment.gross_eur_less_discount = round2(g * (1 - d / 100));
        } else if (hasD && hasA && d !== 100) {
          payment.amount = round2(a / (1 - d / 100));
        }
      } else if (aChanged && !gChanged && !dChanged) {
        if (hasG && hasA && g !== 0) {
          payment.discount = round2((1 - a / g) * 100);
        } else if (hasD && hasA && d !== 100) {
          payment.amount = round2(a / (1 - d / 100));
        }
      }
    });

    nextTick(() => {
      _discountCalcLock.value = false;
    });
  },
  { deep: true }
);

/* Choose Document */
const chooseDocument = (event: any, index: number) => {
  let fileToUpload = event.target.files[0];
  let size = fileToUpload.size / 1024;
  if (size > 20000) {
    isUploadError.value = true;
    return;
  }
  isUploadError.value = false;
  payments.value[index].document = fileToUpload;
  deletedFileId.value = null;

  var reader = new FileReader();
  reader.readAsDataURL(fileToUpload);
  if (fileToUpload.type.includes('pdf')) {
    reader.onload = async () => {
      let response = await fetch(`${reader.result}`);
      let pdfBlob = await response.blob();

      const blobUrl = URL.createObjectURL(pdfBlob);
      payments.value[index].url =
        "<embed src='" +
        blobUrl +
        "' type='application/pdf' frameBorder='0' scrolling='auto' width='100%' height='700px' />";
    };
  }
  if (fileToUpload.type.includes('image')) {
    reader.onload = async () => {
      let response = await fetch(`${reader.result}`);
      let pdfBlob = await response.blob();

      const blobUrl = URL.createObjectURL(pdfBlob);
      payments.value[index].url =
        "<img src='" + blobUrl + "' alt='preview' style='width: 100%' class='img-fluid' />";
    };
  }
  onUploadDocx(index, fileToUpload.name, fileToUpload.size);
};

const deleteAttachment = (index: number) => {
  payments.value[index].document = null;
  payments.value[index].url = null;
  let deleted = payments.value[index].files.shift();
  deletedFileId.value = deleted.id;
};

/* Mapping all errors messages to respected field */
const showErrorMessageToField = (errors: []) => {
  errors.map((error: any) => {
    if (error.attr.includes('.')) {
      let array = error.attr.split('.');
      let errorPaymentIndex = array[0];
      let errorPaymentField = array[1];
      let message = error.detail;

      paymentErrors.value[errorPaymentIndex][errorPaymentField] = message;
    } else {
      isEditSuccessfully.value = false;
      errorMessage.value = error.detail;
    }
  });
};

const openAdjustmentModal = () => {
  isPaymentAdjustment.value = true;
};

const closeAdjustmentModal = () => {
  isPaymentAdjustment.value = false;
  paymentsNeedAdjustment.value = [];
};

const adjustPaymentsAndSubmit = async (data: Object) => {
  Object.entries(data).forEach(([key, value]) => {
    const paymentIdx = payments.value.findIndex((payment: any) => payment.id == key);
    let payment_allocation: any = [];
    value.forEach((d: any) => {
      if (d.allocated_amount !== null && d.allocated_amount !== '' && d.allocated_amount !== 0) {
        payment_allocation.push({
          order: d.id,
          amount: d.allocated_amount,
        });
      } else {
        payment_allocation.push({
          order: d.id,
          amount: 0,
        });
      }
    });
    if (paymentIdx !== -1) payments.value[paymentIdx].payment_allocation = payment_allocation;
  });
  if (isEditPayment.value) {
    await performEdit();
  } else {
    await performCreate();
  }
  closeAdjustmentModal();
};

/* Submit the data */
const onSubmit = async () => {
  isErrorFound.value = false;
  isCreatedSuccessfully.value = null;

  let error = false;
  payments.value.map((item: any, index: any) => {
    mandatoryFields.value.map((field: any) => {
      if (item[field] === null || item[field] === '') {
        error = true;
        isErrorFound.value = true;
        paymentErrors.value[index][field] = t('common.forms.required_field');
      }
    });
  });
  if (error) {
    isCreatedSuccessfully.value = false;
    errorMessage.value = t('common.titles.fill_all_fields');

    return;
  }

  // loading = true;
  payments.value.map((item: any) => {
    item.project = formData.project;
    item.customer = formData.customer;
    item.end_after = formData.endsAfter;
    item.frequency = formData.frequency;
    item.name = formData.invoiceName;
    item.is_recurring = formData.requestType === 'one-time' ? false : true;
    item.start_date =
      formData.startDate !== null ? moment(formData.startDate).format('YYYY-MM-DD') : null;
    item.submit = true;
    item.payment_date =
      item.payment_date !== null ? moment(item.payment_date).format('YYYY-MM-DD') : null;
    item.invoice_date =
      item.invoice_date !== null ? moment(item.invoice_date).format('YYYY-MM-DD') : null;
    item.due_date = item.due_date !== null ? moment(item.due_date).format('YYYY-MM-DD') : null;
    item.service_date =
      item.service_date !== null ? moment(item.service_date).format('YYYY-MM-DD') : null;
    item.status = currentUser.value?.is_admin ? 'APPROVED' : 'PENDING_APPROVAL';
  });

  /* Check Validations */
  $v.value.$touch();

  if (!$v.value.$invalid) {
    // manual validation to check if payment amount matches order amounts
    payments.value.forEach((payment: any) => {
      let paymentAmountCents = payment.amount * 100;
      let orderAmountCents = payment.total_order_balance * 100;
      if (paymentAmountCents === orderAmountCents) {
        return;
      }
      paymentsNeedAdjustment.value.push({
        payment: payment,
        excessAmount: (paymentAmountCents - orderAmountCents) / 100,
      });
    });
    if (paymentsNeedAdjustment.value.length > 0) {
      openAdjustmentModal();
      return;
    }
    if (isEditPayment.value) {
      await performEdit();
    } else {
      await performCreate();
    }
  }
};

const performCreate = async () => {
  try {
    const response = await createPayment(
      payments.value.map((item: any) => {
        delete item.total_order_balance;
        delete item.orders_verbose;
        delete item.id;
        return item;
      })
    );
    await uploadAttachmentsForCreatedPayments(response);
    isCreatedSuccessfully.value = true;
    router.push({ name: 'payments' });
  } catch (error: any) {
    if (error.response.data.type === 'validation_error') {
      isErrorFound.value = true;
      showErrorMessageToField(error.response.data.errors);
    } else {
      isCreatedSuccessfully.value = false;
      if (error.response?.status >= 500) {
        errorMessage.value = t('common.errors.something_went_wrong');
      } else {
        errorMessage.value =
          error.response?.data?.errors?.[0]?.detail || t('common.errors.something_went_wrong');
      }
    }
  }
};

const performEdit = async () => {
  try {
    let data = await editPayment(editPaymentId.value, payments.value[0]);
    if (data) {
      await syncPaymentAttachmentChanges(data.data.id);
      isEditSuccessfully.value = true;
      setTimeout(() => {
        router.push({ name: 'payments' });
      }, 1000);
    } else {
      isEditSuccessfully.value = false;
    }
  } catch (error: any) {
    if (error.response.data.type === 'validation_error') {
      isErrorFound.value = true;
      showErrorMessageToField(error.response.data.errors);
    } else {
      isEditSuccessfully.value = false;
      errorMessage.value = error.response.data.errors[0].detail;
      setTimeout(() => {
        isEditSuccessfully.value = null;
      }, 2000);
    }
  }
};

const onEdit = async () => {
  isErrorFound.value = false;
  isEditSuccessfully.value = null;
  let error = false;
  payments.value.map((item: any, index: any) => {
    mandatoryFields.value.map((field: any) => {
      if (item[field] === null || item[field] === '') {
        error = true;
        isErrorFound.value = true;
        paymentErrors.value[index][field] = t('common.forms.required_field');
      }
    });
  });
  if (error) {
    isEditSuccessfully.value = false;
    errorMessage.value = t('common.forms.fill_all_fields');
    return;
  }

  payments.value.map((item: any) => {
    item.project = formData.project;
    item.customer = formData.customer;
    item.end_after = formData.endsAfter;
    item.frequency = formData.frequency;
    item.name = formData.invoiceName;
    item.parent = null;
    item.is_recurring = formData.requestType === 'one-time' ? false : true;
    item.start_date =
      formData.startDate !== null ? moment(formData.startDate).format('YYYY-MM-DD') : null;
    item.payment_date =
      item.payment_date !== null ? moment(item.payment_date).format('YYYY-MM-DD') : null;
    item.invoice_date =
      item.invoice_date !== null ? moment(item.invoice_date).format('YYYY-MM-DD') : null;
    item.due_date = item.due_date !== null ? moment(item.due_date).format('YYYY-MM-DD') : null;
    item.service_date =
      item.service_date !== null ? moment(item.service_date).format('YYYY-MM-DD') : null;
    item.status = currentUser.value?.is_admin ? 'APPROVED' : 'PENDING_APPROVAL';
    item.submit = true;
  });

  /* Check Validations */
  $v.value.$touch();

  if (!$v.value.$invalid) {
    // manual validation to check if payment amount matches order amounts
    payments.value.forEach((payment: any) => {
      let paymentAmountCents = payment.amount * 100;
      let orderAmountCents = payment.total_order_balance * 100;
      if (paymentAmountCents === orderAmountCents) {
        return;
      }
      paymentsNeedAdjustment.value.push({
        payment: payment,
        excessAmount: (paymentAmountCents - orderAmountCents) / 100,
      });
    });
    if (paymentsNeedAdjustment.value.length > 0) {
      openAdjustmentModal();
      return;
    }
    await performEdit();
  }
};

const closePopup = () => {
  isCancelPopup.value = false;
};

const onChangePaymentCancel = async () => {
  closePopup();
  router.push({ name: 'payments' });
};

/* on Cancel */
const onCancel = () => {
  isCancelPopup.value = true;
};

/* on Draft */
const onDraft = async () => {
  payments.value.map((item: any) => {
    item.project = formData.project;
    item.customer = formData.customer;
    item.end_after = formData.endsAfter;
    item.frequency = formData.frequency;
    item.name = formData.invoiceName;
    item.is_recurring = false;
    item.start_date =
      formData.startDate !== null ? moment(formData.startDate).format('YYYY-MM-DD') : null;
    item.payment_date =
      item.payment_date !== null ? moment(item.payment_date).format('YYYY-MM-DD') : null;
    item.invoice_date =
      item.invoice_date !== null ? moment(item.invoice_date).format('YYYY-MM-DD') : null;
    item.due_date = item.due_date !== null ? moment(item.due_date).format('YYYY-MM-DD') : null;
    item.service_date =
      item.service_date !== null ? moment(item.service_date).format('YYYY-MM-DD') : null;
    item.status = 'DRAFT';
    item.submit = false;
    item.payment_allocation = item.orders.map((order: any) => {
      return {
        order,
        amount: 0, // allocate 0 for now since it's a draft
      };
    });
    delete item.orders_verbose;
  });
  try {
    const response = await createPayment(payments.value);
    await uploadAttachmentsForCreatedPayments(response);
    isDraftSuccessfully.value = true;
    setTimeout(() => {
      router.push({ name: 'payments' });
    }, 1000);
  } catch (error: any) {
    if (error.response.data.type === 'validation_error') {
      isErrorFound.value = true;
      showErrorMessageToField(error.response.data.errors);
    } else {
      errorMessage.value = error.response.data.errors[0].detail;
      isDraftSuccessfully.value = false;
      setTimeout(() => {
        isDraftSuccessfully.value = null;
      }, 2000);
    }
  }
};

/* on Draft Edit */
const onDraftEdit = async () => {
  payments.value.map((item: any) => {
    item.project = formData.project;
    item.customer = formData.customer;
    item.end_after = formData.endsAfter;
    item.frequency = formData.frequency;
    item.name = formData.invoiceName;
    item.is_recurring = false;
    item.parent = null;
    item.start_date =
      formData.startDate !== null ? moment(formData.startDate).format('YYYY-MM-DD') : null;
    item.payment_date =
      item.payment_date !== null ? moment(item.payment_date).format('YYYY-MM-DD') : null;
    item.invoice_date =
      item.invoice_date !== null ? moment(item.invoice_date).format('YYYY-MM-DD') : null;
    item.due_date = item.due_date !== null ? moment(item.due_date).format('YYYY-MM-DD') : null;
    item.service_date =
      item.service_date !== null ? moment(item.service_date).format('YYYY-MM-DD') : null;
    item.status = 'DRAFT';
    item.submit = false;
    item.payment_allocation = item.orders.map((order: any) => {
      return {
        order,
        amount: 0, // allocate 0 for now since it's a draft
      };
    });
  });

  try {
    let { data } = await editPayment(editPaymentId.value, payments.value[0]);
    if (data) {
      await syncPaymentAttachmentChanges(data.id);
    }
    isDraftSuccessfully.value = true;
    setTimeout(() => {
      router.push({ name: 'payments' });
    }, 1000);
  } catch (error: any) {
    if (error.response.data.type === 'validation_error') {
      isErrorFound.value = true;
      showErrorMessageToField(error.response.data.errors);
    } else {
      isDraftSuccessfully.value = false;
      errorMessage.value = error.response.data.errors[0].detail;
      setTimeout(() => {
        isDraftSuccessfully.value = null;
      }, 2000);
    }
  }
};

/* Submit / Draft wrappers — drive the in-flight spinner on the action buttons */
const isMainSubmitting = ref(false);
const isSavingDraft = ref(false);

const runSubmit = async () => {
  if (isMainSubmitting.value || isSavingDraft.value) return;
  isMainSubmitting.value = true;
  try {
    await (isEditPayment.value ? onEdit() : onSubmit());
  } finally {
    isMainSubmitting.value = false;
  }
};

const runDraft = async () => {
  if (isMainSubmitting.value || isSavingDraft.value) return;
  isSavingDraft.value = true;
  try {
    await (isEditPayment.value ? onDraftEdit() : onDraft());
  } finally {
    isSavingDraft.value = false;
  }
};

const supplierSelectRef = ref<any>(null);

const updateSelectedOrders = (newValue: any) => {
  payments.value[currentindex.value].orders = [];
  payments.value[currentindex.value].orders_verbose = [];
  payments.value[currentindex.value].comparison_gross_eur_order_list = 0;
  payments.value[currentindex.value].total_order_balance = 0;
  payments.value[currentindex.value].sequence_order_no = '';
  newValue.map((order: any) => {
    payments.value[currentindex.value].orders.push(order.id);
    payments.value[currentindex.value].orders_verbose.push(order);
    payments.value[currentindex.value].comparison_gross_eur_order_list += +order.amount;
    payments.value[currentindex.value].total_order_balance += +order.current_balance;
  });

  console.log(newValue);

  let orderSum = payments.value.reduce(
    (total, item) => total + item.comparison_gross_eur_order_list,
    0
  );

  remainingBalance.value = currentBalance.value - orderSum;
};

const getCurrentIndex = (newValue: any) => {
  currentindex.value = newValue;
};

const openNewSupplierModal = (currentIndex: number) => {
  isSupplierPopup.value = true;
  paymentSupplierIndex.value = currentIndex;
  setFieldValue('customer', formData.customer);
};
const closeNewSupplierModal = () => {
  isSupplierPopup.value = false;
  resetForm();
};

const addNewSupplier = handleSubmit(async values => {
  try {
    const supplierResponse: any = await createSupplier({
      name: values.supplierName,
      supplier_number: values.supplierNumber,
      address: values.address,
      zip_code: values.zipCode!,
      city: values.city!,
      country: values.country!,
      phone_number: values.phone,
      email: values.supplierEmail,
      customer: formData.customer,
    });

    payments.value[paymentSupplierIndex.value].supplier = supplierResponse.id;
    payments.value[paymentSupplierIndex.value].order_no_supplier = supplierResponse.supplier_number;

    resetForm();
    closeNewSupplierModal();
    const selectInstance = Array.isArray(supplierSelectRef.value)
      ? supplierSelectRef.value[paymentSupplierIndex.value]
      : supplierSelectRef.value;
    await selectInstance?.fetchSuppliers();
  } catch (error: any) {
    supplierCreatedSuccessfully.value = false;
    errorSupplierMessage.value =
      error?.response?.data?.errors?.[0]?.detail || error?.message || 'An error occurred';
  }
});

onMounted(async () => {
  isEditPayment.value = !!route.params.id;

  setBreadcrumbs([
    {
      labelKey: 'sidebar.items.payments',
      to: { name: 'payments' },
    },
    {
      label: isEditPayment.value ? t('common.forms.edit') : t('payment.titles.new_payment'),
    },
  ]);

  await getCustomersOptions().then(response => {
    customersList.value = response;
    let getSelectedCustomerID = cookieService.get('selectedCustomerId');
    let customerId = getSelectedCustomerID ? JSON.parse(getSelectedCustomerID) : -1;
    if (customerId !== -1) formData.customer = customerId;
  });

  if (formData.customer) {
    newCustomerId.value = formData.customer;
  }

  if (isEditPayment.value) {
    let paymentId = route.params.id as string;
    formData.paymentCount = 0;
    payments.value = [];
    getPaymentById(paymentId).then(response => {
      payments.value.push(response);
      payments.value.map((payment: any) => {
        if (payment.customer.id !== formData.customer) {
          router.push({ name: 'payments' });
        }
        editPaymentId.value = payment.id;
        formData.customer = payment.customer.id;
        formData.project = payment.project?.id || null;
        formData.orderCount = 1;
        formData.name = payment.name === null ? '' : payment.name;
        formData.startDate = payment.start_date;
        formData.requestType = payment.is_recurring ? 'recurring' : 'one-time';
        formData.frequency = payment.frequency === null ? '' : payment.frequency;
        formData.endsAfter = payment.end_after === null ? 0 : payment.end_after;
        paymentOrders.value = payment.orders;
        selectedOrdersChangeKey.value++; // force table rerender
        payment.order_no_supplier = payment.supplier?.supplier_number;
        payment.supplier = payment.supplier?.id;

        payment.url = payment.files.length > 0 ? payment.files[0].file : null;
        if (payment.url !== null) {
          if (payment.files.length > 0 && payment.files[0].file.includes('.pdf')) {
            payment.url =
              "<embed src='" +
              payment.url +
              "' type='application/pdf' frameBorder='0' scrolling='auto' width='100%' height='700px' />";
          } else {
            payment.url =
              "<img src='" +
              payment.url +
              "' alt='preview' style='width: 100%' class='img-fluid' />";
          }
        }
      });
      formData.paymentCount = payments.value.length;
    });
  }
});

const onUploadDocx = (index: any, fileName: string, fileSize: string) => {
  if (fileCount.value === 0) {
    newFiles.value[index].file = payments.value[index].document;
    newFiles.value[index].index = index;
    newFiles.value[index].file_name = fileName;
    newFiles.value[index].file_size = fileSize;
  } else {
    newFiles.value.push({
      file: payments.value[index].document,
      index: index,
      file_name: fileName,
      file_size: fileSize,
    });
  }
  fileCount.value++;
};

const formatDate = (date: Date) => {
  return moment(date).format('DD.MM.YYYY');
};
</script>
